import { GroveMoneyMapCalculatorButton } from '@fruitful/earnings-calculator';
import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitConfig,
    type IncomeSplitConfigLegacy,
} from '@fruitful/income-split';
import { getCategorizedRulesAndTotals } from '@fruitful/money-map';
import {
    AccountCategory,
    ApexAccountState,
    ApexDerivedSubState,
    type ImAccount,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapApexAccountDetails,
    type MoneyMapSyncteraExternalAccountDetails,
    Portfolio,
    RiskLevel,
    SupportedApexAccountRegistrationType,
    TimeHorizon,
} from '@fruitful/types';
import { Stack } from 'expo-router';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { Dimensions } from 'react-native';
import type { ScaledSize } from 'react-native/Libraries/Utilities/Dimensions';
import { FullScreenSpinner, LabelRegular, XStack } from 'smoothie';

import FruitfulLogo from '../../../assets/fruitful-logo.svg';
import FullScreenError from '../../common/components/FullScreenError';
import { Button } from '../../common/components/GroveButtons';
import { selectedMembershipAtom } from '../../entities/entityAtoms';
import type { TrackedEvent } from '../../logger/Events';
import { captureException, trackEvent } from '../../logger/Logger';
import { useGetMoneyMap } from '../hooks/useGetMoneyMap';
import useGetProposedIncomeSplit from '../hooks/useGetProposedIncomeSplit';
import useGetProposedLegacyIncomeSplit from '../hooks/useGetProposedLegacyIncomeSplit';
import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import { MoneyMapSimulatorButton } from './MoneyMapSimulatorButton';
import { PreviewAccounts } from './preview/PreviewAccounts';
import { PreviewGoals } from './preview/PreviewGoals';
import { PreviewIncomeSplit } from './preview/PreviewIncomeSplit';
import { PreviewVisual } from './preview/PreviewVisual';
import { Sidebar, SIDEBAR_WIDTH } from './preview/Sidebar';

interface MoneyMapPreviewPageWithHeaderProps {
    moneyMapId: string;
}

const MoneyMapPreviewPageWithHeader = ({
    moneyMapId,
}: MoneyMapPreviewPageWithHeaderProps) => {
    const [moneyMapToggle, setMoneyMapToggle] = useState(true);
    const [goalsToggle, setGoalsToggle] = useState(false);
    const [accountsToggle, setAccountsToggle] = useState(false);
    const [incomeSplitToggle, setIncomeSplitToggle] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [screenWidth, setScreenWidth] = useState(
        Dimensions.get('window').width,
    );

    const hasValidMoneyMapId =
        moneyMapId != null && moneyMapId !== '' && moneyMapId !== 'undefined';

    const {
        data: moneyMap,
        isLoading: isLoadingMoneyMap,
        refetch: refetchMoneyMap,
    } = useGetMoneyMap(
        {
            id: moneyMapId,
        },
        { enabled: hasValidMoneyMapId },
    );

    const { accounts, refetch: refetchAccounts } = useMoneyMapAccounts();

    const moneyMapRulesWithProposedAccounts = (
        moneyMap?.moneyMapRules ?? []
    ).filter((moneyMapRule) => {
        const underlyingAccount =
            moneyMapRule.moneyMapAccount.moneyMapAccountForTransfer ??
            moneyMapRule.moneyMapAccount;
        return underlyingAccount?.status === MoneyMapAccountStatus.PROPOSED;
    });

    const membership = useAtomValue(selectedMembershipAtom);
    const holisticSummary = membership?.holisticSummary;

    const moneyMapHasGroups = (moneyMap?.moneyMapRuleGroups ?? []).length > 0;

    const {
        data: incomeSplitLegacy,
        isLoading: isLoadingProposedIncomeSplitLegacy,
    } = useGetProposedLegacyIncomeSplit(
        {
            membershipId: membership?.id ?? '',
            moneyMapId,
        },
        { enabled: moneyMap != null && !moneyMapHasGroups },
    );

    const { data: incomeSplit, isLoading: isLoadingProposedIncomeSplit } =
        useGetProposedIncomeSplit(moneyMap);

    const membershipExternalAccounts: DepositAccount[] = accounts
        .filter((account) => account.externalAccountId != null)
        .map((account) => {
            const { name, logo, syncteraAccountId } =
                account.accountDetails as MoneyMapSyncteraExternalAccountDetails;
            return {
                id: syncteraAccountId,
                nickname: name,
                logo: logo ?? '',
                isExternal: true,
            };
        });

    // Build investment accounts for income split display
    // Income split rules use apex account IDs (from holisticSummary) to match accounts
    // We only need apexAccountId, nickname, icon, and color - everything else is unused
    const internalInvestmentAccounts: ImAccount[] = (
        holisticSummary?.[AccountCategory.INVEST]?.accounts ?? []
    ).map((holisticAccount): ImAccount => {
        // Find matching money map account to get icon/color if available
        const matchingMoneyMapAccount = accounts.find(
            (mmAccount) =>
                mmAccount.type ===
                    MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
                (mmAccount.accountDetails as MoneyMapApexAccountDetails)
                    .name === holisticAccount.name,
        );

        const accountDetails = matchingMoneyMapAccount?.accountDetails as
            | MoneyMapApexAccountDetails
            | undefined;

        return {
            apexAccountId: holisticAccount.id, // Matches rule.accountId in income split rules
            nickname:
                accountDetails?.name ??
                holisticAccount.name ??
                'Investment Account',
            icon: holisticAccount.logo?.split(':')[0] ?? null,
            color: holisticAccount.logo?.split(':')[1] ?? null,
            // Required ImAccount fields (unused by income split utils)
            accountNumber: holisticAccount.accountNumberMasked ?? '',
            realtimeBalanceData: {
                accountEquityCents: 0,
                positionMarketValueCents: 0,
                cashCents: 0,
            },
            contributionSummaries: [],
            contributionConstraints: null,
            apexPortfolio: Portfolio.CORE,
            apexRiskLevel: RiskLevel.ONE,
            apexTimeHorizon: TimeHorizon.AVERAGE,
            enrollments: [],
            registrationType:
                SupportedApexAccountRegistrationType.RegistrationIndividual,
            ownershipType:
                holisticAccount.jointOwnerUserId != null
                    ? 'JOINT'
                    : 'INDIVIDUAL',
            state: ApexAccountState.OPEN,
            createdAt:
                matchingMoneyMapAccount?.createdAt ?? new Date().toISOString(),
            isRetirementAccount: false,
            canDepositFunds: true,
            canWithdrawFunds: true,
            derivedSubState: ApexDerivedSubState.OPEN_ACTIVE,
            backdoorRothRole: null,
        };
    });

    // Update the screenWidth on window resize events.
    useEffect(() => {
        const onChange = ({ window }) => {
            setScreenWidth((window as ScaledSize).width);
        };

        const subscription = Dimensions.addEventListener('change', onChange);

        return () => {
            subscription.remove();
        };
    }, []);

    const {
        billRules,
        spendRules,
        goalRules,
        otherAccountRules,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = useMemo(() => {
        return getCategorizedRulesAndTotals(moneyMap, accounts);
    }, [JSON.stringify(accounts), JSON.stringify(moneyMap)]);

    const refresh = async () => {
        setIsRefreshing(true);

        try {
            await Promise.all([refetchMoneyMap(), refetchAccounts()]);
        } catch (error) {
            captureException(error);
        } finally {
            setIsRefreshing(false);
        }
    };

    if (!hasValidMoneyMapId) {
        captureException(
            new Error('Money map preview opened without a valid id'),
        );
        return <FullScreenError />;
    }

    if (
        isLoadingMoneyMap ||
        isRefreshing ||
        isLoadingProposedIncomeSplitLegacy ||
        isLoadingProposedIncomeSplit
    ) {
        return <FullScreenSpinner />;
    }

    if (moneyMap == null) {
        captureException(new Error('Money map not found'));
        return <FullScreenError />;
    }

    const availableScreenWidth = screenWidth - SIDEBAR_WIDTH;

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => (
                        <XStack marginHorizontal={24}>
                            <FruitfulLogo width={32} height={32} />
                        </XStack>
                    ),
                    headerTitle: () => (
                        <LabelRegular>{moneyMap.title}</LabelRegular>
                    ),
                    headerTitleAlign: 'center',
                    headerRight: () => (
                        <XStack marginHorizontal={16} gap={8}>
                            <Button
                                variant="secondary"
                                size="tiny"
                                target="_blank"
                                onPress={async () => {
                                    await refresh();
                                }}
                            >
                                Refresh
                            </Button>
                            {membership?.holisticSummary && (
                                <GroveMoneyMapCalculatorButton
                                    moneyMap={moneyMap}
                                    holisticSummary={membership.holisticSummary}
                                    appContext="grove-money-map-preview"
                                    onInteractionEvent={(eventName, data) => {
                                        trackEvent(
                                            eventName as TrackedEvent,
                                            data,
                                        );
                                    }}
                                />
                            )}
                            <MoneyMapSimulatorButton
                                moneyMap={moneyMap}
                                accounts={accounts}
                            />
                        </XStack>
                    ),
                }}
            />
            <Sidebar
                moneyMapToggle={moneyMapToggle}
                onToggleMoneyMap={() => {
                    setMoneyMapToggle(!moneyMapToggle);
                }}
                accountsToggle={accountsToggle}
                onToggleAccounts={() => {
                    setAccountsToggle(!accountsToggle);
                }}
                goalsToggle={goalsToggle}
                onToggleGoals={() => {
                    setGoalsToggle(!goalsToggle);
                }}
                incomeSplitToggle={incomeSplitToggle}
                onToggleIncomeSplit={() => {
                    setIncomeSplitToggle(!incomeSplitToggle);
                }}
            />
            <XStack
                flex={1}
                alignSelf="flex-end"
                width={availableScreenWidth}
                backgroundColor="$white"
            >
                {moneyMapToggle && (
                    <PreviewVisual
                        moneyMap={moneyMap}
                        accounts={accounts}
                        billsTotalCents={billsTotalCents}
                        spendTotalCents={spendTotalCents}
                        goalsTotalCents={goalsTotalCents}
                    />
                )}
                {goalsToggle && (
                    <PreviewGoals
                        moneyMap={moneyMap}
                        accounts={accounts}
                        goalRules={goalRules}
                        goalsTotalCents={goalsTotalCents}
                    />
                )}
                {accountsToggle && (
                    <PreviewAccounts
                        moneyMap={moneyMap}
                        accounts={accounts}
                        billRules={billRules}
                        spendRules={spendRules}
                        goalRules={goalRules}
                        otherAccountRules={otherAccountRules}
                        billsTotalCents={billsTotalCents}
                        spendTotalCents={spendTotalCents}
                        goalsTotalCents={goalsTotalCents}
                    />
                )}
                {incomeSplitToggle && (incomeSplit ?? incomeSplitLegacy) && (
                    <PreviewIncomeSplit
                        incomeMoneyMapAccount={moneyMap.incomeMoneyMapAccount}
                        accounts={accounts as BankAccountDisplayDetail[]}
                        externalAccounts={membershipExternalAccounts ?? []}
                        internalInvestmentAccounts={internalInvestmentAccounts}
                        moneyMapRulesWithProposedAccounts={
                            moneyMapRulesWithProposedAccounts
                        }
                        incomeSplit={
                            moneyMapHasGroups
                                ? (incomeSplit as IncomeSplitConfig)
                                : (incomeSplitLegacy as IncomeSplitConfigLegacy)
                        }
                        moneyMapHasGroups={moneyMapHasGroups}
                    />
                )}
            </XStack>
        </>
    );
};

export default MoneyMapPreviewPageWithHeader;
